/**
 * Compatibility wrapper: next/link → TanStack Router Link
 *
 * Maps Next.js Link (href prop) to TanStack Router Link (to prop).
 * This allows existing code to work without changing every <Link href="..."> to <Link to="...">.
 *
 * Migration note: After the initial migration stabilizes, replace imports of this
 * compat module with direct '@tanstack/react-router' imports and change href→to.
 */
import { Link as TanStackLink } from '@tanstack/react-router';
import type { ReactNode, AnchorHTMLAttributes } from 'react';

interface CompatLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string;
  children?: ReactNode;
  /** Preserve the old Next.js opt-out while using TanStack intent preloading. */
  prefetch?: boolean;
  /** Next.js scroll behavior */
  scroll?: boolean;
  /** Next.js replace navigation */
  replace?: boolean;
}

function Link({ href, prefetch, scroll: _scroll, replace, className, children, ...rest }: CompatLinkProps) {
  return (
    <TanStackLink
      to={href}
      replace={replace}
      preload={prefetch === false ? false : 'intent'}
      className={className}
      {...rest}
    >
      {children}
    </TanStackLink>
  );
}

export default Link;
